'use client';

import { useRouter } from '@bprogress/next/app';
import Image from 'next/image';
import Link from 'next/link';
import { useTranslations } from 'next-intl';

import { ProductStockStatus } from '@/fo/api/magento';
import StarIcon from '@/fo/icons/base/icons/star.svg';
import { UiKitCheckboxBase } from '@/fo/ui/kit/checkbox/base';
import { UiKitLabelBase } from '@/fo/ui/kit/label/base';
import { UiKitLabelBrand } from '@/fo/ui/kit/label/brand';
import { UiLoyaltyInformerCount } from '@/fo/ui/loyalty/informer/count';
import { UiProductButtonBuy } from '@/fo/ui/product/button/buy';
import { UiProductButtonSubscribe } from '@/fo/ui/product/button/subscribe';
import { UiProductOptionsBase } from '@/fo/ui/product/options/base';
import { UiProductWishlistButton } from '@/fo/ui/product/wishlist/button';
import { getMediaBaseUrl } from '@/fo/util/image-loader';
import {
  getProductCatalogAlt,
  Option,
  Label,
  StockStatus
} from '@/fo/util/map-product-data';

import { styles } from './UiProductTileBase.styles';

const ETAT_PUR_BRAND_NAME = 'Etat Pur';

/** Очищает HTML-теги из строки для использования в title */
const stripHtmlTags = (html: string): string => {
  return html.replace(/<[^>]*>/g, '');
};

export interface UiProductTileBaseProps {
  ref?: React.RefObject<HTMLElement | null>;
  className?: string;
  isInCart?: boolean;
  isInWishlist?: boolean;
  /**
   * Ссылка на страницу товара
   *
   * Кликабельные элементы внутри плитки должны иметь z-index выше, чем slots.link
   */
  link: string;
  /** Изображение товара */
  image: string;
  /** Название бренда */
  brand?: {
    color: string;
    name: string;
    url: string;
  };
  /** ID товара в mindbox */
  mindboxId: string;
  /** Статус наличия товара */
  stockStatus: StockStatus;
  /** Название товара */
  title: string;
  /** Описание товара */
  description: string;
  /** Название серии */
  series: string;
  /** Опции товара (объем, цвет и т.д.) */
  options?: Option[];
  /** Объем */
  volume?: string;
  /** Текущая цена */
  price: number | null;
  /** Старая цена */
  oldPrice?: number | null;
  /** Рейтинг */
  rating?: number;
  /** Количество отзывов */
  reviewCount?: number;
  /** Средний рейтинг */
  bestRating?: number;
  /** Худший рейтинг */
  worstRating?: number;
  /** Количество баллов лояльности */
  bonuses?: number;
  bonusesMax?: number;
  /** Лейбл баллов лояльности */
  bonusesLabel?: string;
  /** Авторизован ли пользователь */
  isAuthorized?: boolean;
  /** Метки для товара (скидки, хиты и т.д.) */
  labels?: Label[];
  /** Выбран ли товар (для чекбокса) */
  isSelected?: boolean;
  /** Обработчик клика по товару */
  onClick?: () => void;
  /** Обработчик клика по опции */
  onOptionClick?: (option: Option) => void;
  /** Обработчик клика по корзине */
  onCartClick?: () => void;
  /** Обработчик клика по избранному */
  onWishlistClick?: () => void;
  /** Обработчик клика по подписке на продукт */
  onSubscribeClick?: () => void;
  /** Обработчик изменения чекбокса */
  onSelect?: (selected: boolean) => void;
  /** Загрузка кнопки корзины */
  loadingCartButton?: boolean;
  isCatalog?: boolean;
  /** Изображение текстуры средства */
  structureImage?: string;
  /** Максимальное количество опций */
  maxOptionsVisible?: number;
  /** Максимальное количество опций на мобильном */
  maxOptionsVisibleMobile?: number;
  /** Включить микроразметку Schema.org */
  enableMicrodata?: boolean;
  /** Минимальная цена для микроразметки */
  lowPrice?: number | null;
  /** Максимальная цена для микроразметки */
  highPrice?: number | null;
}

export function UiProductTileBase({
  ref,
  className,
  isInCart = false,
  isInWishlist = false,
  link,
  image,
  brand,
  mindboxId,
  stockStatus,
  title,
  description,
  series,
  options,
  price,
  oldPrice,
  rating = 0,
  reviewCount = 0,
  bestRating = 0,
  bonuses = 0,
  bonusesMax = 0,
  bonusesLabel,
  isAuthorized,
  labels,
  isSelected,
  onClick,
  onOptionClick,
  onCartClick,
  onWishlistClick,
  onSubscribeClick,
  onSelect,
  loadingCartButton,
  isCatalog,
  structureImage,
  maxOptionsVisible = 3,
  maxOptionsVisibleMobile = 2,
  enableMicrodata = false,
  lowPrice,
  highPrice
}: UiProductTileBaseProps) {
  const t = useTranslations();
  const router = useRouter();
  const slots = styles({
    wishlist: isInWishlist,
    catalog: isCatalog
  });
  const isOutOfStock = stockStatus === ProductStockStatus.OutOfStock;

  const isEtatPur = brand?.name === ETAT_PUR_BRAND_NAME;
  const imageAlt = getProductCatalogAlt({
    series,
    name: title,
    brand: brand?.name
  });

  return (
    <article
      ref={ref}
      className={slots.base({ className })}
      {...(enableMicrodata && {
        itemScope: true,
        itemType: 'https://schema.org/Product'
      })}
    >
      {/* Скрытые метаданные для микроразметки */}
      {enableMicrodata && (
        <>
          <meta
            itemProp="url"
            content={link}
          />

          <div
            itemProp="brand"
            itemScope
            itemType="https://schema.org/Brand"
            content="Naos"
            style={{ display: 'contents' }}
          >
            <meta
              itemProp="name"
              content="Naos"
            />
          </div>
        </>
      )}

      <div className={slots.imageContainer()}>
        <div className={slots.imageWrapper()}>
          <Image
            className={slots.image()}
            width={310}
            height={310}
            src={image}
            alt={imageAlt}
            {...(enableMicrodata && { itemProp: 'image' })}
          />
          {!!structureImage && structureImage !== 'no_selection' && (
            <Image
              className={slots.structureImage()}
              width={310}
              height={310}
              src={`${getMediaBaseUrl()}/catalog/product/${structureImage}`}
              alt={imageAlt}
              {...(enableMicrodata && { itemProp: 'image' })}
            />
          )}
          <UiProductWishlistButton
            className={slots.wishlist()}
            isActive={isInWishlist}
            onClick={onWishlistClick}
          />
          {onSelect && (
            <div className={slots.checkbox()}>
              <UiKitCheckboxBase
                checked={isSelected}
                onChange={onSelect}
              />
            </div>
          )}
        </div>
        {labels && labels.length > 0 && (
          <div className={slots.labels()}>
            {labels.map((label, index) => (
              <UiKitLabelBase
                key={index}
                customColor={label.color}
              >
                {label.name}
              </UiKitLabelBase>
            ))}
          </div>
        )}
        {isOutOfStock && (
          <UiKitLabelBase
            color="black"
            className={slots.outOfStockLabel()}
          >
            {t('Out of stock')}
          </UiKitLabelBase>
        )}
      </div>

      <div className={slots.content()}>
        <Link
          className={slots.link()}
          href={link}
          onClick={onClick}
          title={stripHtmlTags(`${series} ${title}`)}
        >
          {title}
        </Link>

        <div
          className={slots.header({
            className: isOutOfStock ? 'opacity-60' : ''
          })}
        >
          <div className={slots.brandContainer()}>
            {brand?.url && (
              <UiKitLabelBrand
                color={brand?.color}
                url={brand?.url}
              >
                {brand.name}
              </UiKitLabelBrand>
            )}
          </div>

          {(rating > 0 || enableMicrodata) && (
            <div
              className={rating > 0 ? slots.rating() : 'sr-only'}
              {...(enableMicrodata && {
                itemProp: 'aggregateRating',
                itemScope: true,
                itemType: 'https://schema.org/AggregateRating'
              })}
            >
              {enableMicrodata && (
                <>
                  <meta
                    itemProp="bestRating"
                    content={(reviewCount > 0 && bestRating > 0
                      ? bestRating
                      : 5
                    ).toString()}
                  />
                  <meta
                    itemProp="reviewCount"
                    content={(reviewCount > 0 ? reviewCount : 1).toString()}
                  />
                  <meta
                    itemProp="ratingValue"
                    content={(reviewCount > 0 ? rating : 5).toString()}
                  />
                </>
              )}

              {rating > 0 && (
                <>
                  <StarIcon className={slots.starIcon()} />
                  <span>{rating}</span>
                </>
              )}
            </div>
          )}
        </div>

        <div className={slots.options()}>
          {options && options.length > 0 && (
            <UiProductOptionsBase
              options={options.map((option) => ({
                ...option,
                isSelected: isOutOfStock ? false : option.isSelected
              }))}
              size="s"
              onOptionClick={(option) => {
                onOptionClick?.(option);
              }}
              maxVisible={maxOptionsVisible}
              maxVisibleMobile={maxOptionsVisibleMobile}
              noSelected
            />
          )}
        </div>

        <div className={slots.bottom()}>
          <h2 className={slots.info()}>
            <span
              className={slots.title()}
              dangerouslySetInnerHTML={{
                __html: isEtatPur ? title : `${series} ${title}`
              }}
              title={stripHtmlTags(`${series} ${title}`)}
              onClick={() => router.push(link)}
              {...(enableMicrodata && { itemProp: 'name' })}
            />
            <span className="sr-only"> {brand?.name} </span>
            <span
              className={slots.description()}
              dangerouslySetInnerHTML={{ __html: description }}
              suppressHydrationWarning
              {...(enableMicrodata && { itemProp: 'description' })}
            />
          </h2>

          <div className={slots.actions()}>
            <UiLoyaltyInformerCount
              className={`${!bonusesMax || isOutOfStock ? 'opacity-0' : ''} ${slots.informer()}`}
              bonuses={bonuses}
              bonusesMax={bonusesMax}
              label={bonusesLabel}
              isAuthorized={isAuthorized}
              showLogin={false}
            />

            {/* Микроразметка для предложения (цена, наличие) */}
            {enableMicrodata && highPrice && (
              <div
                itemProp="offers"
                itemScope
                itemType={`https://schema.org/${
                  lowPrice != null && lowPrice !== highPrice
                    ? 'AggregateOffer'
                    : 'Offer'
                }`}
                style={{ display: 'contents' }}
              >
                <meta
                  itemProp="priceCurrency"
                  content="RUB"
                />

                {lowPrice != null && lowPrice !== highPrice ? (
                  <>
                    <meta
                      itemProp="lowPrice"
                      content={String(Math.round(lowPrice))}
                    />
                    <meta
                      itemProp="highPrice"
                      content={String(Math.round(highPrice))}
                    />
                  </>
                ) : (
                  <meta
                    itemProp="price"
                    content={String(Math.round(lowPrice ?? highPrice))}
                  />
                )}

                <link
                  itemProp="availability"
                  href={
                    isOutOfStock
                      ? 'https://schema.org/OutOfStock'
                      : 'https://schema.org/InStock'
                  }
                />
                <meta
                  itemProp="url"
                  content={link}
                />
              </div>
            )}

            {isOutOfStock && mindboxId && (
              <UiProductButtonSubscribe
                onClick={onSubscribeClick}
                className={slots.actionButton()}
              />
            )}

            {price !== null && !isOutOfStock && (
              <UiProductButtonBuy
                isInCart={isInCart}
                className={slots.actionButton()}
                classNamePrice={slots.buttonBuyPrice()}
                classNamePriceValue={slots.buttonBuyPriceValue()}
                price={price}
                oldPrice={oldPrice}
                onClick={onCartClick}
                loading={loadingCartButton}
              />
            )}
          </div>
        </div>
      </div>
    </article>
  );
}
